import { useState } from "react";
import moment from "moment-timezone";
import Lightbox from "components/ui/Lightbox";
import Modal from "components/ui/Modal";
import Button from "components/ui/Button";
import {
  useCancelSocialPostMutation,
  useDeleteSocialPostMutation,
  useUpdateSocialPostMutation,
} from "../../apis/socialPost";
import {
  canDeleteSocialPost,
  canCancelSocialPost,
  isVideoMedia,
  postMediaUrls,
} from "utils/socialPost";

const SYDNEY_TIMEZONE = "Australia/Sydney";

// The date input wants a local wall clock; the post is scheduled in Sydney.
const toScheduleInputValue = (scheduleDate) =>
  scheduleDate
    ? moment(scheduleDate).tz(SYDNEY_TIMEZONE).format("YYYY-MM-DDTHH:mm")
    : "";

const SocialPostDetails = ({ post, onDone }) => {
  const [activeTab, setActiveTab] = useState("details");
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [caption, setCaption] = useState(post?.caption || "");
  const [scheduleInput, setScheduleInput] = useState(
    toScheduleInputValue(post?.scheduleDate)
  );
  const [appliesToRun, setAppliesToRun] = useState(true);
  const [confirmAction, setConfirmAction] = useState(null);

  const { mutate: updatePost, isPending: isSaving } =
    useUpdateSocialPostMutation();
  const { mutateAsync: cancelPost, isPending: isCancelling } =
    useCancelSocialPostMutation();
  const { mutateAsync: deletePost, isPending: isDeleting } =
    useDeleteSocialPostMutation();

  // The row can disappear underneath this modal once it is deleted.
  if (!post) return null;

  const media = postMediaUrls(post.media);
  const runDeliveries = post.runDeliveries ?? 1;
  const runCancellable = post.runCancellable ?? 0;
  const hasRun = Boolean(post.runId) && runDeliveries > 1;
  const canCancel = canCancelSocialPost(post);
  const canDelete = canDeleteSocialPost(post);
  const scope = hasRun && appliesToRun ? "run" : "delivery";

  const handleEditOpen = () => {
    setCaption(post.caption || "");
    setScheduleInput(toScheduleInputValue(post.scheduleDate));
    setIsEditing(true);
  };

  const handleSave = () => {
    // A date left untouched is not sent: a past time would be refused.
    const scheduleChanged =
      Boolean(scheduleInput) &&
      scheduleInput !== toScheduleInputValue(post.scheduleDate);
    updatePost(
      {
        id: post._id,
        data: {
          caption,
          ...(scheduleChanged && {
            scheduleDate: moment
              .tz(scheduleInput, "YYYY-MM-DDTHH:mm", SYDNEY_TIMEZONE)
              .toISOString(),
          }),
          scope,
        },
      },
      { onSuccess: () => setIsEditing(false) }
    );
  };

  const handleConfirm = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (action === "cancel") await cancelPost({ id: post._id, scope });
    if (action === "delete") await deletePost({ id: post._id, scope });
    onDone?.();
  };

  const scopeNote = hasRun
    ? appliesToRun
      ? `all ${runDeliveries} deliveries in this post`
      : "this one delivery"
    : "this delivery";

  return (
    <div className="overflow-hidden">
      <div className="bg-lightgray p-4 border-b border-lightgray">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
          <h3>
            {post.platform
              ? post.platform.charAt(0).toUpperCase() + post.platform.slice(1)
              : "Instagram"}{" "}
            {post.type}
          </h3>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 rounded-full bg-black text-white">
              {(post.status || "unknown").toUpperCase()}
            </span>
            <span>
              {post.scheduleDate
                ? `Scheduled for ${moment(post.scheduleDate)
                    .tz(SYDNEY_TIMEZONE)
                    .format("D MMM YYYY, h:mm A")}`
                : "No schedule date"}
            </span>
          </div>
        </div>
        {hasRun && (
          <p className="text-darkergray text-sm mt-2">
            Part of one post sent to {runDeliveries} deliveries
            {runCancellable > 0
              ? ` — ${runCancellable} still to go out.`
              : " — none still to go out."}
          </p>
        )}
      </div>

      <div className="border-b border-lightgray">
        <nav className="flex">
          <button
            onClick={() => setActiveTab("details")}
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === "details"
                ? "border-b-2 border-black text-black"
                : "text-darkgray hover:text-darkergray"
            }`}
          >
            Details
          </button>
          <button
            onClick={() => setActiveTab("media")}
            className={`px-4 py-3 text-sm font-medium ${
              activeTab === "media"
                ? "border-b-2 border-black text-black"
                : "text-darkgray hover:text-darkergray"
            }`}
          >
            Post Media ({media.length})
          </button>
          {/* Admin posts have no property, so no marketing materials exist —
              the tab used to render and crash on the null. */}
          {post.marketing && (
            <button
              onClick={() => setActiveTab("marketing")}
              className={`px-4 py-3 text-sm font-medium ${
                activeTab === "marketing"
                  ? "border-b-2 border-black text-black"
                  : "text-darkgray hover:text-darkergray"
              }`}
            >
              Marketing Materials
            </button>
          )}
        </nav>
      </div>

      <div className="p-4">
        {activeTab === "details" && (
          <div className="space-y-6">
            <div>
              <h4>User Information</h4>
              <div className="bg-lightgray p-3 rounded-md">
                <p className="text-black font-medium">
                  {post.userId?.name ?? "No Agent"}
                </p>
                <p className="text-darkergray">{post.userId?.email}</p>
                {post.userId?.instagramUsername && (
                  <p className="text-darkergray text-sm">
                    Instagram: @{post.userId.instagramUsername}
                  </p>
                )}
              </div>
            </div>

            {/* Property Info — absent on admin posts */}
            {post.address && (
              <div>
                <h4>Property</h4>
                <div className="bg-lightgray p-3 rounded-md">
                  <p className="text-black">{post.address}</p>
                </div>
              </div>
            )}

            {post.collaborators?.length > 0 && (
              <div>
                <h4>Collaborators</h4>
                <div className="bg-lightgray p-3 rounded-md">
                  <div className="flex flex-wrap gap-2">
                    {post.collaborators.map((collaborator, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-lightgray rounded-md text-black"
                      >
                        @{collaborator}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {post.skipReason && (
              <div>
                <h4>Reason</h4>
                <div className="bg-lightgray p-3 rounded-md">
                  <p className="text-black">{post.skipReason}</p>
                </div>
              </div>
            )}

            <div>
              <h4>Caption</h4>
              {isEditing ? (
                <div className="space-y-3">
                  <textarea
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                    className="w-full p-2 form-textarea border"
                    rows={6}
                  />
                  <div>
                    <label className="form-label">Scheduled for (Sydney)</label>
                    <input
                      type="datetime-local"
                      value={scheduleInput}
                      onChange={(e) => setScheduleInput(e.target.value)}
                      className="form-input border border-mediumgray w-full"
                    />
                  </div>
                  {hasRun && (
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={appliesToRun}
                        onChange={(e) => setAppliesToRun(e.target.checked)}
                      />
                      <span>
                        Apply to all {runDeliveries} deliveries in this post
                      </span>
                    </label>
                  )}
                  <p className="text-darkergray text-sm">
                    Write <strong>{"{{AGENT_NAME}}"}</strong> to drop each
                    agent's own name into their copy.
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      onClick={handleSave}
                      className="black-button"
                      disabled={isSaving}
                    >
                      {isSaving ? "Saving..." : "Save"}
                    </Button>
                    <Button
                      onClick={() => setIsEditing(false)}
                      className="gray-button"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <p className="text-black whitespace-pre-line">
                    {post.caption || "No caption"}
                  </p>
                  {!canCancel && (
                    <p className="text-darkergray text-sm">
                      This post has already published, so it can no longer be
                      edited.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "media" && (
          <div>
            <h4>Post Media</h4>
            {media.length === 0 ? (
              <div className="bg-lightgray p-3 rounded-md">
                <p className="text-black">No media available for this post</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {media.map((url, index) =>
                  isVideoMedia(post, url) ? (
                    <div
                      key={index}
                      className="relative aspect-square overflow-hidden rounded-md border border-mediumgray"
                    >
                      <video
                        src={encodeURI(url)}
                        className="w-full h-full object-cover"
                        controls
                      />
                    </div>
                  ) : (
                    <div
                      key={index}
                      className="relative aspect-square overflow-hidden rounded-md border border-mediumgray cursor-pointer"
                      onClick={() => setSelectedImage(url)}
                    >
                      <img
                        src={url}
                        alt={`Post media ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        )}

        {activeTab === "marketing" && post.marketing && (
          <div className="space-y-6">
            <div>
              <h4>Marketing Images</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {(post.marketing.social ?? []).map((image) => (
                  <div
                    key={image.id}
                    className="relative aspect-square overflow-hidden rounded-md border border-lightgray cursor-pointer"
                    onClick={() => setSelectedImage(image.url)}
                  >
                    <img
                      src={image.url || "/placeholder.svg"}
                      alt={`Marketing image ${image.id}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4>Edited Videos</h4>
              <div className="space-y-3">
                {(post.marketing.editedVideos ?? []).map((video) => (
                  <div key={video.id} className="bg-lightgray p-3 rounded-md">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                      <p className="font-medium text-black">{video.name}</p>
                      <a
                        href={video.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="black-button text-center"
                      >
                        View Video
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {(canCancel || canDelete) && !isEditing && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-mediumgray p-4">
          {hasRun && (
            <label className="mr-auto flex items-center gap-2">
              <input
                type="checkbox"
                checked={appliesToRun}
                onChange={(e) => setAppliesToRun(e.target.checked)}
              />
              <span>Apply to all {runDeliveries} deliveries</span>
            </label>
          )}
          {canCancel && (
            <Button onClick={handleEditOpen} className="outline-button">
              Edit
            </Button>
          )}
          {canCancel && (
            <Button
              onClick={() => setConfirmAction("cancel")}
              className="gray-button"
              disabled={isCancelling}
            >
              {isCancelling ? "Stopping..." : "Stop this post"}
            </Button>
          )}
          {canDelete && (
            <Button
              onClick={() => setConfirmAction("delete")}
              className="black-button"
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          )}
        </div>
      )}

      <Modal
        isOpen={Boolean(confirmAction)}
        onClose={() => setConfirmAction(null)}
        title={confirmAction === "delete" ? "Delete this post?" : "Stop this post?"}
        className="max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => setConfirmAction(null)}
              className="gray-button"
            >
              Keep it
            </Button>
            <Button onClick={handleConfirm} className="black-button">
              {confirmAction === "delete" ? "Delete" : "Stop it"}
            </Button>
          </div>
        }
      >
        <p>
          {confirmAction === "delete"
            ? `This removes ${scopeNote} from Beleef. Anything that already published stays as a record.`
            : `This stops ${scopeNote} from going out. Anything that already published cannot be pulled back.`}
        </p>
      </Modal>

      <Lightbox
        images={selectedImage ? [{ src: selectedImage }] : []}
        currentIndex={0}
        isOpen={Boolean(selectedImage)}
        onClose={() => setSelectedImage(null)}
      />
    </div>
  );
};

export default SocialPostDetails;
